/**
 * Module dependencies.
 */

import { Button, Image, Type } from 'src/components/core';
import {
  RawHtml,
  color,
  media,
  normalizeNewLine,
  units,
  useBreakpoint
} from '@untile/react-components';

import { ReactElement } from 'react';
import { contentConfig } from 'src/core/content-config/about-us';
import { theme } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import Container from 'src/components/core/layout/container';
import map from 'lodash/map';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
};

/**
 * `currentYear`.
 */

const currentYear = new Date().getFullYear();

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  overflow: hidden;
  padding: ${units(6)} 0 ${units(8)};
  position: relative;
  width: 100vw;

  ${media.min('md')`
    padding: ${units(13)} 0 ${units(18)};
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '.         titleOneWrapper .'
    'timeline  timeline        timeline'
    'cylinders cylinders       cylinders'
    'cylinders cylinders       cylinders'
    'image     image           image'
    '.         titleTwoWrapper .'
    '.         team            .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;
  grid-template-rows: max-content repeat(2, 13.9vw) repeat(4, max-content);
  width: 100%;

  ${media.min('xxs')`
    grid-template-rows: max-content repeat(2, 10.5vw) repeat(4, max-content);
  `}

  ${media.min('md')`
    grid-template-areas:
      'timeline timeline        timeline  timeline timeline timeline        timeline'
      '.        .               .         .        .        titleOneWrapper .'
      '.        cylinders       cylinders .        .        titleOneWrapper .'
      '.        .               .         .        image    image           image'
      '.        .               .         .        image    image           image'
      '.        titleTwoWrapper .         .        .        .               .'
      '.        titleTwoWrapper .         .        .        .               .'
      '.        team            team      team     team     team            .';
    grid-template-columns: 1fr 4fr 1fr ${theme(
      'grid.columnsGutter'
    )}px 1fr 4fr 1fr;
    grid-template-rows: 7.2vw 5.6vw repeat(4, max-content);
  `}
`;

/**
 * `Background` styled component.
 */

const Background = styled.div`
  background-color: ${color('backgrounds.blue02')};
  grid-column: 1 / -1;
  grid-row: 3 / 5;
  z-index: -1;
`;

/**
 * `Timeline` styled component.
 */

const Timeline = styled(Type.H1)`
  color: ${color.transparentize('backgrounds.blue02', 0.2)};
  font-size: 21.4vw;
  grid-area: timeline;
  grid-row: 2 / 4;
  justify-self: end;
  line-height: 21.4vw;
  margin-right: -3vw;
  margin-top: -5vw;
  overflow: hidden;
  position: relative;
  white-space: nowrap;
  z-index: -2;

  ${media.min('ms')`
    margin-top: -4.5vw;
  `}

  ${media.min('md')`
    grid-row: 1 / 3;
    margin-top: -2.5vw;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  margin-bottom: ${units(2)};

  ${media.min('md')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.Paragraph)`
  margin-bottom: ${units(5)};

  ${media.min('md')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  margin-bottom: ${units(6.5)};

  ${media.min('md')`
    margin-bottom: ${units(13)};
  `}
`;

/**
 * `TitleOneWrapper` styled component.
 */

const TitleOneWrapper = styled(TitleWrapper)`
  grid-area: titleOneWrapper;
`;

/**
 * `TitleTwoWrapper` styled component.
 */

const TitleTwoWrapper = styled(TitleWrapper)`
  grid-area: titleTwoWrapper;

  ${media.min('md')`
    margin-top: ${units(14)};
  `}
`;

/**
 * `CylindersWrapper` styled component.
 */

const CylindersWrapper = styled.div`
  grid-area: cylinders;
  margin-bottom: ${units(8)};
  padding-top: ${units(5)};

  ${media.min('md')`
    margin-bottom: ${units(4.5)};
    padding-top: ${units(7)};
  `}
`;

/**
 * `CylindersImageWrapper` styled component.
 */

const CylindersImageWrapper = styled.div`
  overflow: hidden;
  padding-bottom: 67.7%;
  position: relative;

  ${media.min('md')`
    margin: 0 0 ${units(2)} ${units(3)};
  `}
`;

/**
 * `CylindersDescriptionWrapper` styled component.
 */

const CylindersDescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${theme('grid.gutterMobile')}px;
`;

/**
 * `CylindersDescriptionYear` styled component.
 */

const CylindersDescriptionYear = styled(Type.H6)`
  border-bottom: 1px solid ${color('textColor')};
  margin-bottom: ${units(1)};
  width: max-content;
`;

/**
 * `CylindersDescription` styled component.
 */

const CylindersDescription = styled(Type.Paragraph)`
  font-style: italic;
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  grid-area: image;
  overflow: hidden;
  padding-bottom: 69.9%;
  position: relative;

  ${media.max('md')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `TeamWrapper` styled component.
 */

const TeamWrapper = styled.div`
  display: grid;
  grid-area: team;
  grid-gap: ${units(1)} 0;
  grid-template-rows: repeat(7, max-content);
  max-width: ${units(19)};

  ${media.min('sm')`
    grid-gap: ${units(3)};
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: unset;
    max-width: unset;
  `}

  ${media.min('ms')`
    grid-template-columns: repeat(4, 1fr);
  `}

  ${media.min('md')`
    grid-template-columns: repeat(5, 1fr);
  `}

  ${media.min('lg')`
    grid-gap: 0 ${units(3)};
    grid-template-columns: repeat(7, 1fr);
  `}
`;

/**
 * `TeamMemberWrapper` styled component.
 */

const TeamMemberWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * `TeamMemberName` styled component.
 */

const TeamMemberName = styled(Type.H6)`
  border-bottom: 1px solid ${color('grey400')};
  padding-bottom: ${units(1)};

  ${media.min('md')`
    padding-bottom: ${units(2)};
  `}
`;

/**
 * `TeamMemberRole` styled component.
 */

const TeamMemberRole = styled(Type.Paragraph)`
  color: ${color('grey400')};
  padding-top: ${units(1)};

  ${media.min('md')`
    padding-top: ${units(2)};
  `}
`;

/**
 * `HistoryTeamSection` component.
 */

const HistoryTeamSection = (props: Props): ReactElement => {
  const { className } = props;
  const { translate } = useTranslate();
  const isMobile = useBreakpoint('md', 'max');
  const routeResolver = useLocalizedRoute();
  const timeline = `1966-${currentYear}`;
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  return (
    <Wrapper className={className}>
      <StyledContainer>
        <Grid>
          <Background />

          <Timeline
            data-scroll={'fade-in-up-small'}
            data-scroll-offset={'20%'}
          >
            {timeline}
          </Timeline>

          <TitleOneWrapper>
            <Title
              {...animProps}
              data-scroll-animation-delay={'150'}
            >
              {translate('about-us:sections.history.title')}
            </Title>

            <Description {...animProps}>
              {translate('about-us:sections.history.description')}
            </Description>

            <div {...animProps}>
              <Button
                colorTheme={'secondary'}
                href={routeResolver('ourStory')}
              >
                {translate('common:actions.discoverOurTimeline')}
              </Button>
            </div>
          </TitleOneWrapper>

          <CylindersWrapper>
            <CylindersImageWrapper
              data-scroll={'fade-in-up-small'}
              data-scroll-offset={'20%'}
            >
              <Image
                alt={timeline}
                fill
                src={'/static/images/history-blue-cylinders.png'}
                style={{ objectFit: 'cover' }}
              />
            </CylindersImageWrapper>

            <CylindersDescriptionWrapper>
              <CylindersDescriptionYear {...animProps}>
                {'1966'}
              </CylindersDescriptionYear>

              <CylindersDescription {...animProps}>
                {translate('about-us:sections.history.cylinders')}
              </CylindersDescription>
            </CylindersDescriptionWrapper>
          </CylindersWrapper>

          <ImageWrapper
            {...(!isMobile && {
              'data-scroll': true,
              'data-scroll-speed': 1
            })}
          >
            <Image
              alt={translate('about-us:sections.ourTeam.title')}
              fill
              src={'/static/images/manufacture-2.jpg'}
              style={{ objectFit: 'cover' }}
            />
          </ImageWrapper>

          <TitleTwoWrapper>
            <Title {...animProps}>
              {translate('about-us:sections.ourTeam.title')}
            </Title>

            <Description {...animProps}>
              {translate('about-us:sections.ourTeam.description')}
            </Description>

            <div {...animProps}>
              <Button
                colorTheme={'secondary'}
                href={routeResolver('humanResources')}
              >
                {translate('common:actions.joinOurTeam')}
              </Button>
            </div>
          </TitleTwoWrapper>

          <TeamWrapper>
            {map(contentConfig.teamMembers, ({ name, role }, index) => (
              <TeamMemberWrapper
                {...animProps}
                key={index}
              >
                <TeamMemberName>{name}</TeamMemberName>

                <RawHtml element={TeamMemberRole}>
                  {normalizeNewLine(role)}
                </RawHtml>
              </TeamMemberWrapper>
            ))}
          </TeamWrapper>
        </Grid>
      </StyledContainer>
    </Wrapper>
  );
};

/**
 * Export `HistoryTeamSection` component.
 */

export default HistoryTeamSection;
